import { BookOpen, FileText, FileUp, FolderCog, LibraryBig, Languages, Mic, Newspaper, Plus, Tag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LibraryCategoryBadge } from "@/components/library/LibraryCategoryBadge";
import { libraryCopy } from "@/lib/library";
import type { LibraryCategory as LCFull } from "@/lib/libraryCategories";

type Props = {
  defaultCategory: LCFull | null;
  isNeutralDefault: boolean;
  sourceTypeCounts: Record<string, number>;
  languageSummary: string[];
  categoriesCount: number;
  onImport: () => void;
  onManageCategories: () => void;
  onCreate: () => void;
};

/** Page header for /my-library: title row + action buttons + stat badges. */
export const MyLibraryHeader = ({
  defaultCategory,
  isNeutralDefault,
  sourceTypeCounts,
  languageSummary,
  categoriesCount,
  onImport,
  onManageCategories,
  onCreate,
}: Props) => (
  <div className="space-y-3">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-3">
          <LibraryBig className="h-6 w-6 text-primary" />
          <h1 className="font-serif text-3xl font-semibold">My Library</h1>
        </div>
        <p className="max-w-2xl text-sm text-muted-foreground">Search, save, and organize source records.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={onImport}>
          <FileUp className="h-4 w-4" /> Import
        </Button>
        <Button variant="outline" onClick={onManageCategories}>
          <FolderCog className="h-4 w-4" /> Manage categories
        </Button>
        <Button onClick={onCreate}>
          <Plus className="h-4 w-4" /> {libraryCopy.manualAction}
        </Button>
      </div>
    </div>

    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
      {defaultCategory ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-1.5">
          <span className="text-xs uppercase tracking-wide">Default</span>
          <LibraryCategoryBadge category={defaultCategory} className="py-0.5" />
          {isNeutralDefault ? <span className="text-xs text-muted-foreground">Neutral baseline</span> : null}
        </div>
      ) : null}

      <Badge variant="outline" className="gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium">
        <LibraryBig className="h-3.5 w-3.5 text-primary" />
        <span className="text-muted-foreground">Books</span>
        <span className="text-foreground">{sourceTypeCounts.book ?? 0}</span>
      </Badge>
      <Badge variant="outline" className="gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium">
        <Newspaper className="h-3.5 w-3.5 text-primary" />
        <span className="text-muted-foreground">Articles</span>
        <span className="text-foreground">{sourceTypeCounts.journal_article ?? 0}</span>
      </Badge>
      <Badge variant="outline" className="gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium">
        <BookOpen className="h-3.5 w-3.5 text-primary" />
        <span className="text-muted-foreground">Chapters</span>
        <span className="text-foreground">{sourceTypeCounts.chapter ?? 0}</span>
      </Badge>
      <Badge variant="outline" className="gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium">
        <FileText className="h-3.5 w-3.5 text-primary" />
        <span className="text-muted-foreground">Papers</span>
        <span className="text-foreground">{sourceTypeCounts.proceedings_article ?? 0}</span>
      </Badge>
      <Badge variant="outline" className="gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium">
        <Mic className="h-3.5 w-3.5 text-primary" />
        <span className="text-muted-foreground">Episodes</span>
        <span className="text-foreground">{sourceTypeCounts.podcast ?? 0}</span>
      </Badge>
      <Badge variant="outline" className="gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium">
        <Languages className="h-3.5 w-3.5 text-primary" />
        <span className="text-muted-foreground">Languages</span>
        <span className="text-foreground">{languageSummary.length}</span>
      </Badge>
      <Badge variant="outline" className="gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium">
        <Tag className="h-3.5 w-3.5 text-primary" />
        <span className="text-muted-foreground">Categories</span>
        <span className="text-foreground">{categoriesCount}</span>
      </Badge>

    </div>
  </div>
);
